import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import BreadcrumbLink from "../../../components/Common/BreadcrumbLink";
import AlertSuccess from "../../../components/Alerts/AlertSuccess";
import LoaderRombo from "../../../components/Common/LoaderRombo";
import logoBlack from "../../../assets/images/logo-black.png";
import i18n from "../../../i18n";
import { ServiceStatus, ServiceEventType } from "../../../constants/enums";
import { getAddressInfo, minimumLoadingTime, orgId } from "../../../constants/utils";
import { getServiceAdmin, getServiceRequestAdmin, getTripsTimeline, getTripsResume } from "../../../helpers/api_rombo_helper";
import { GeneralInfo, ServiceDetails, Passengers, Addresses, Actions } from "./utils";

const DetailService = ({ serviceCode, serviceStatus, isPopup, onClose }) => {
  document.title = `${i18n.t("ServiceDetail")} - ${orgId}`;

  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [textHeader, setTextHeader] = useState("");
  const [textBody, setTextBody] = useState("");
  const [infoUser, setInfoUser] = useState({ permissions: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [timelineData, setTimelineData] = useState([]);
  const [gpxData, setGpxData] = useState(null);
  const [service, setService] = useState({
    scheduled_start: {},
    stops: [],
    riders: [],
    url_image: "",
  });

  const actualServiceCode = serviceCode || id;

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data,
    })
  );

  const { token, loading, data } = useSelector(LoginProperties);

  useEffect(() => {
    const startTime = Date.now();
    const fetchServices = async () => {
      let useReservationsEndpoint = false;

      if (isPopup) {
        useReservationsEndpoint = serviceStatus && serviceStatus !== ServiceStatus.UNASSIGNED;
      } else {
        useReservationsEndpoint =
          location.pathname.includes("/services/reservations") ||
          location.pathname.includes("/services/assignment") ||
          location.pathname.includes("/services/historical");
      }

      if (useReservationsEndpoint) {
        return getServiceAdmin(token, actualServiceCode, {});
      } else {
        return getServiceRequestAdmin(token, actualServiceCode, {});
      }
    };

    fetchServices()
      .then(async (resp) => {
        setService(resp);
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(minimumLoadingTime - elapsedTime, 0);

        try {
          const timelineResponse = await getTripsTimeline(token, { booking_code: resp.code });
          if (timelineResponse && timelineResponse.trip) {
            const flatTimeline = timelineResponse.trip.map(obj => Object.values(obj)[0]);
            setTimelineData(flatTimeline);
          }
        } catch (timelineError) {
          console.error("Error getting timeline:", timelineError);
        }
  
        setTimeout(() => setIsLoading(false), remainingTime);
      })
      .catch((error) => {
        console.error(`Error - ${error}`);
        setIsLoading(false);
      });
  }, [loading, location.pathname, token, actualServiceCode, isPopup]);  

  const { origin, destination, scales } = useMemo(() => getAddressInfo(service), [service]);

  const getGpxBaseUrl = () => {
    const env = import.meta.env.VITE_APP_DEFAULTAUTH;
    return env === 'dev' 
      ? 'https://s3.us-west-1.amazonaws.com/app-pt-trips.stg' 
      : 'https://s3.us-west-1.amazonaws.com/app-pt-trips.prod';
  };

  useEffect(() => {
    if (!service?.code || service?.status !== ServiceStatus.FINISHED) return;
  
    const fetchTripsResume = async () => {
      try {
        const resumeResponse = await getTripsResume(service.code, 'gpx');
        
        if (resumeResponse && resumeResponse.url) {
          const response = await fetch(resumeResponse.url, { method: 'HEAD' });
          if (response.ok) {
            setGpxData(resumeResponse.url);
            return;
          }
        }
        
        const normalizedCode = service.code.toLowerCase().replace(/-/g, '_').replace(/^tp_/, '');
        const gpxBaseUrl = getGpxBaseUrl();
        const gpxUrl = `${gpxBaseUrl}/tp_${normalizedCode}.gpx`;
        
        const fallbackResponse = await fetch(gpxUrl, { method: 'HEAD' });
        if (fallbackResponse.ok) {
          setGpxData(gpxUrl);
        } else {
          console.warn("GPX file not found in S3");
          setGpxData(null);
        }
      } catch (error) {
        console.error("Failed to fetch trip summary:", {
          error: error.message,
          status: error.response?.status,
        });
        setGpxData(null);
      }
    };
  
    fetchTripsResume();
  }, [service.code, service?.status]);  

  const getEventDetails = (event) => {
    if (!event) {
      return {
        title: i18n.t("UnknownEvent"),
        description: i18n.t("NoDetailsAvailable"),
        icon: "fas fa-question-circle",
        status: "pending"
      };
    }

    if (event.action === "asigment") {
      const isUnassign = event.type === "unassign";
      const driverName = event.name?.replace(/^#/, "") || i18n.t("UnknownDriver");
      const plate = event.plate?.replace(/^#/, "") || i18n.t("UnknownVehicle");
    
      return {
        title: isUnassign 
          ? i18n.t("ServiceRequestUnassigned") 
          : i18n.t("ServiceRequestAssigned"),
        description: (
          <>
            <div><strong>{i18n.t("Driver")}:</strong> {driverName}</div>
            <div><strong>{i18n.t("Vehicle")}:</strong> {plate}</div>
          </>
        ),
        icon: isUnassign ? "fas fa-user-minus" : "fas fa-user-plus",
        status: isUnassign ? "cancelled" : "completed"
      };
    }    
  
    if (event.action) {
      switch (event.action) {
        case "request":
          return null; 
        case "stop":
          if (event.type === ServiceEventType.ORIGIN_PICKUP) {
            if (![ServiceStatus.STARTED, ServiceStatus.FINISHED].includes(service.status)) {
              return null;
            }
            return {
              title: i18n.t("OriginPickup"),
              description: event.status 
                ? (event.status === "PENDING" ? i18n.t("Pending") : i18n.t("Completed"))
                : i18n.t("StatusUnknown"),
              icon: "fas fa-map-marker-alt",
              status: event.status === "PENDING" ? "pending" : "completed"
            };
          } else if (event.type === ServiceEventType.DESTINATION_DROPOFF) {
            if (service.status !== ServiceStatus.FINISHED) {
              return null;
            }
            return {
              title: i18n.t("DestinationDropoff"),
              description: event.status
                ? (event.status === "PENDING" ? i18n.t("Pending") : i18n.t("Completed"))
                : i18n.t("StatusUnknown"),
              icon: "fas fa-flag-checkered",
              status: event.status === "PENDING" ? "pending" : "completed"
            };
          }
          break;
        default:
          return {
            title: event.action || i18n.t("Event"),
            description: event.status || i18n.t("StatusUnknown"),
            icon: "fas fa-circle",
            status: "pending"
          };
      }
    }
    
    switch (event.type) {
      case ServiceEventType.CREATED:
        return {
          title: i18n.t("ServiceRequestCreated"),
          description: `${i18n.t("ScheduledFor")} ${event.date || i18n.t("DateUnknown")}`,
          icon: "fas fa-calendar-plus",
          status: "completed"
        };
      case ServiceEventType.STARTED:
        return {
          title: i18n.t("ServiceStarted"),
          description: service.driver?.driver_name 
          ? (
            <>
              <strong>{i18n.t("Driver")}:</strong> {service.driver.driver_name}
            </>
          )
        : i18n.t("ServiceStarted"),
          icon: "fas fa-play-circle",
          status: "completed"
        };
      case ServiceEventType.COMPLETED:
        return {
          title: i18n.t("ServiceCompleted"),
          description: service.trip?.time
            ? `${i18n.t("Duration")}: ${service.trip.time}`
            : i18n.t("ServiceCompleted"),
          icon: "fas fa-check-circle",
          status: "completed"
        };
      case ServiceEventType.CANCELLED:
        return {
          title: i18n.t("ServiceRequestCancelled"),
          description: service.cancellation_info?.reason || i18n.t("NoCancellationReasonProvided"),
          icon: "fas fa-times-circle",
          status: "cancelled",
        };
      default:
        return {
          title: event.type || i18n.t("Event"),
          description: event.description || "",
          icon: "fas fa-circle",
          status: "pending"
        };
    }
  };

  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr && !timeStr) return "";
  
    if (typeof dateStr === "string" && dateStr.includes("de")) {
      return dateStr;
    }
  
    try {
      let combinedDateTime = dateStr || '';
      if (timeStr) {
        const trimmedTime = timeStr.split(".")[0]; 
        combinedDateTime = dateStr ? `${dateStr}T${trimmedTime}` : trimmedTime;
      }
  
      const date = new Date(combinedDateTime);
      if (isNaN(date.getTime())) return "";
  
      const day = date.getDate().toString().padStart(2, "0");
      const month = date.toLocaleString("es-ES", { month: "long" });
      const year = date.getFullYear();
  
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      const formattedHours = hours.toString().padStart(2, "0");
  
      return `${day} de ${month.charAt(0).toUpperCase() + month.slice(1)} de ${year} - ${formattedHours}:${minutes} ${ampm}`;
    } catch (e) {
      console.error("Error al formatear la fecha:", e);
      return "";
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadcrumbLink title={i18n.t("Services")} link="/services/request" breadcrumbItem={i18n.t("ServiceDetail")} />
        <Row className="p-2 bg-white">
          <Col xxl={6} md={6} xl={6} xs={12} className="d-flex">
            <img src={logoBlack} alt="logo" height="120" />
          </Col>
          <Col xxl={6} md={6} xl={6} xs={12} className="text-end d-flex justify-content-end align-items-start">
            {`${i18n.t("Service")} # ${service.code} - ${i18n.t(`${service.type}`)}`}
          </Col>
          <div className="separator" />
        </Row>
        {isLoading ? (
          <Row className="p-2 bg-white d-flex justify-content-center align-items-center">
            <LoaderRombo />
          </Row>
        ) : (
          <>
            <Row className="bg-white d-flex align-items-start tabs-container">
              <Button
                onClick={() => setActiveTab("general")}
                className={`tab-button ${activeTab === "general" ? "active" : ""}`}
              >
                {i18n.t("General")}
              </Button>
              <Button
                onClick={() => setActiveTab("details")}
                className={`tab-button ${activeTab === "details" ? "active" : ""}`}
              >
                {i18n.t("ServiceDetail")}
              </Button>
              <Button
                onClick={() => setActiveTab("passengers")}
                className={`tab-button ${activeTab === "passengers" ? "active" : ""}`}
              >
                {i18n.t("Passengers")}
              </Button>
              <Button
                onClick={() => setActiveTab("addresses")}
                className={`tab-button ${activeTab === "addresses" ? "active" : ""}`}
              >
                {i18n.t("Addresses")}
              </Button>
              <Button
                onClick={() => setActiveTab("actions")}
                className={`tab-button ${activeTab === "actions" ? "active" : ""}`}
              >
                {i18n.t("Actions")}
              </Button>
            </Row>
            <Row className="p-4 bg-white">
              {activeTab === "general" && <GeneralInfo service={service} />}
              {activeTab === "details" && <ServiceDetails service={service} />}
              {activeTab === "passengers" && <Passengers service={service} />}
              {activeTab === "addresses" && (
                <Addresses 
                  service={service} 
                  origin={origin} 
                  destination={destination} 
                  scales={scales}
                  gpxData={gpxData}
                />
              )}
              {activeTab === "actions" && (
                <Actions 
                  service={service} 
                  timelineData={timelineData}
                  getEventDetails={getEventDetails}
                  formatDateTime={formatDateTime}
                />
              )}
            </Row>
            {!isPopup && (
              <Row className="p-2 bg-white">
                <Col className="text-end">
                  <button
                    onClick={() => navigate(-1)}
                    className="btn btn-go-back mx-2"
                  >
                    {i18n.t("GoBack")}
                  </button>
                </Col>
              </Row>
            )}
          </>
        )}
        <AlertSuccess
          showAlertSuccess={showAlertSuccess}
          setShowAlertSuccess={setShowAlertSuccess}
          textHeader={textHeader}
          textBody={textBody}
        />
      </div>
    </div>
  );
};

export default DetailService;
